// SCR-CHAR, MOD-STATS, MOD-LEVEL-UP — E05-T05, ux/05 §1–2, ux/03 §6.
// Надетое видно на фигуре сразу после ответа equip; на потолке уровня — «Опыт мастера: {n}» из masterXp.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Item, Slot } from '../content/types.js';
import { useApp } from '../app/app.js';
import { BOSSES, MOVES, SLOT_NAMES, STAT_NAMES, itemName } from '../content/catalog.js';
import { num } from '../i18n/format.js';
import { clientConfig, inventory, online, player, saveCache, wallet } from '../state/game.js';
import { bossInfo } from '../content/catalog.js';
import { stage, type ScreenProps, type SheetProps } from '../ui/App.js';
import { ItemTile, ScreenTitle, Sheet, Skeleton, slotGlyph, useActionBar } from '../ui/kit.js';
import { assetEntry, assetUrl, manifest, markBroken, pickFile } from '../assets/manifest.js';
import { useLoad } from '../ui/use-load.js';
import { I } from '../assets/icons.js';
import { t as tr } from '../i18n/t.js';
import { Count } from '../ui/text-fx.js';
import { SetsChip } from './sets.js';
import { RespecLink } from './respec.js';

const LEFT: Slot[] = ['head', 'jacket', 'pants'];
const RIGHT: Slot[] = ['shoes', 'tool', 'accessory'];
export const STAT_KEYS = ['str', 'agi', 'vit', 'cha'] as const;
export const STAT_LABEL: Record<(typeof STAT_KEYS)[number], string> = { str: tr('UI-STAT-STR'), agi: tr('UI-STAT-DEX'), vit: tr('UI-STAT-VIT'), cha: tr('UI-STAT-CHA') };
/** Уровни открытия приёмов (ux/04 §4.2) — для «что открылось» в MOD-LEVEL-UP. */
export const MOVE_LEVELS: Record<string, number> = { chem: 3, torch: 5, kit: 6, jack: 12, weld: 22 };

/** «Справность» — сумма характеристик надетого для показа (ux/05 §1.1; формула — геймдизайн, здесь только оценка). */
export function gearScore(items: Item[]): number {
  return Math.round(items.filter((i) => i.equipped).reduce((s, i) => s + (i.stats.attack ?? 0) * 2 + (i.stats.hp ?? 0) * 0.5 + (i.stats.def ?? 0) * 3 + ((i.stats.crit ?? 0) + (i.stats.dodge ?? 0)) * 1000, 0));
}

export function useInventory() {
  const app = useApp();
  return useLoad(() => app.call('getInventory'), { cached: inventory.value, onData: (d) => { inventory.value = d; saveCache(); } });
}

/** Кадр куклы: FIG-hero-BODY и слои DOLL-* — 389 × 389 px @1x (art-prod, кукла). */
const DOLL_FRAME = 389;
/** Пустой слот — вещь героя по умолчанию; обувь и куртка нужны всегда: под ними у тела вырезаны ступни и руки. */
const DOLL_DEFAULT: Partial<Record<Slot, string>> = {
  head: 'DOLL-EQ-HEAD-01', jacket: 'DOLL-DEFAULT-JACKET', pants: 'DOLL-EQ-PANTS-01', shoes: 'DOLL-DEFAULT-SHOES', tool: 'DOLL-EQ-TOOL-01',
};
const DOLL_SLOTS: Slot[] = ['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory'];

/** Слои куклы для надетого: id ассета по слоту (надетая вещь, иначе вещь по умолчанию), по возрастанию z. */
export function dollLayers(equipped: readonly Pick<Item, 'slot' | 'defId'>[], has: (id: string) => { z?: number } | null | undefined): string[] {
  const ids = DOLL_SLOTS.flatMap((slot) => {
    const it = equipped.find((i) => i.slot === slot);
    const own = it ? `DOLL-${it.defId}` : null;
    const id = own && has(own) ? own : DOLL_DEFAULT[slot];
    return id && has(id) ? [id] : [];
  });
  return ids.sort((x, y) => (has(x)?.z ?? 0) - (has(y)?.z ?? 0));
}

/**
 * Кукла (art-prod, кукла): основа FIG-hero-BODY — тот же кадр, что FIG-hero, герой без вещей; поверх — слои
 * DOLL-<ID предмета> по возрастанию z в точке pos кадра. Без основы или без слоёв — цельная FIG-hero.
 */
export function Doll(p: { equipped: Item[] }) {
  void manifest.value;
  const bodyUrl = assetUrl('FIG-hero-BODY');
  const layers = bodyUrl ? dollLayers(p.equipped, assetEntry) : [];
  const baseUrl = bodyUrl && layers.length ? bodyUrl : assetUrl('FIG-hero');
  const pct = (v: number) => `${(v / DOLL_FRAME) * 100}%`;
  return (
    <div class="figure" aria-label={tr('CL-SCREENS-CHAR-01')} data-testid="doll">
      <div class="doll">
        {baseUrl ? <img class="doll-base" src={baseUrl} alt="" /> : null}
        {baseUrl === bodyUrl ? layers.map((id) => {
          const e = assetEntry(id)!;
          const url = assetUrl(id);
          if (!url || !e.pos) return null;
          return <img key={id} class="doll-part" src={url} alt="" data-part={e.slot} onError={() => markBroken(id)} style={{ left: pct(e.pos[0]), top: pct(e.pos[1]), width: pct(pickFile(e, 1).w ?? 0) }} />;
        }) : null}
      </div>
    </div>
  );
}

function SlotCell(p: { slot: Slot; item: Item | undefined; hasSpare: boolean }) {
  const app = useApp();
  if (p.item) return <ItemTile item={p.item} testId={`slot-${p.slot}`} onClick={() => app.nav.openSheet('MOD-ITEM', { itemId: p.item!.itemId })} />;
  return (
    <button class="item-tile empty-slot" data-testid={`slot-${p.slot}`} aria-label={tr('CL-SCREENS-CHAR-02', { p0: SLOT_NAMES[p.slot] })} onClick={() => app.nav.push('SCR-INVENTORY', { slot: p.slot })}>
      <span class="glyph" style={{ opacity: 0.4 }} aria-hidden>{slotGlyph(p.slot)}</span>
      <span class="nm">{p.hasSpare ? '+' : SLOT_NAMES[p.slot]}</span>
    </button>
  );
}

export function CharScreen(_p: ScreenProps) {
  const app = useApp();
  const inv = useInventory();
  const me = player.value;
  useEffect(() => { void stage.value?.show('none'); }, [stage.value]);
  useEffect(() => { app.call('getMe').then((p) => { player.value = p; saveCache(); }).catch(() => {}); }, []);
  const items = inv.data?.items ?? [];
  const bySlot = new Map(items.filter((i) => i.equipped).map((i) => [i.slot, i]));
  const free = me?.statPointsFree ?? 0;
  useActionBar({
    secondary: { text: tr('CL-SCREENS-CHAR-03'), tone: 'outline', testId: 'open-bag', onClick: () => app.nav.push('SCR-INVENTORY') },
    ...(free > 0 ? { main: { text: tr('CL-SCREENS-CHAR-04'), testId: 'spend-points', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-STATS') } } : {}),
  });
  const capped = me && me.xpToNext === null;
  return (
    <>
      <ScreenTitle right={<><SetsChip /><button class="chip" data-testid="open-profile" onClick={() => app.nav.push('SCR-GARAGE-VISIT', { publicId: me?.publicId })}>{tr('UI-NAV-PROFILE')}</button></>}>
        {me?.displayName ?? tr('CL-SCREENS-CHAR-06')} <span class="small muted">{tr('CL-SCREENS-CHAR-05')} {me?.level ?? '—'}</span>
      </ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-CHAR">
        <div class="page col char-page">
          {me ? (
            capped
              ? <div class="small" data-testid="master-xp">{tr('CL-SCREENS-CHAR-07')}{" "}{num(wallet.value?.masterXp ?? 0)}</div>
              : <div class="col" style={{ gap: 4 }}><div class="progress"><i style={{ width: `${Math.min(100, (me.xp / (me.xpToNext || 1)) * 100)}%` }} /></div><span class="small muted">{num(me.xp)} / {num(me.xpToNext ?? 0)} {" "}{tr('CL-SCREENS-CHAR-08')}</span></div>
          ) : <Skeleton h={24} />}
          <div class="char-figure">
            <div class="col slots">{LEFT.map((s) => <SlotCell key={s} slot={s} item={bySlot.get(s)} hasSpare={items.some((i) => i.slot === s && !i.equipped)} />)}</div>
            <Doll equipped={[...bySlot.values()]} />
            <div class="col slots">{RIGHT.map((s) => <SlotCell key={s} slot={s} item={bySlot.get(s)} hasSpare={items.some((i) => i.slot === s && !i.equipped)} />)}</div>
          </div>
          <button class="char-strip" data-testid="stats-row" onClick={() => app.nav.openSheet('MOD-STATS')}>
            <span class="cs-cell cs-power"><span>{tr('UI-CHAR-POWER')}</span><b data-testid="gear-score">{num(gearScore(items))}</b></span>
            {STAT_KEYS.map((k) => <span key={k} class="cs-cell"><span>{STAT_LABEL[k]}</span><b>{me?.stats[k] ?? '—'}</b></span>)}
            {me ? (
              <span class="cs-derived">
                {(['attack', 'hp', 'def', 'crit', 'dodge'] as const).map((k) => <span key={k}>{STAT_NAMES[k]} <b>{k === 'crit' || k === 'dodge' ? `${(me.derived[k] * 100).toFixed(1)}%` : num(me.derived[k])}</b></span>)}
              </span>
            ) : null}
            {free > 0 ? <span class="cs-free badge">{tr('CL-SCREENS-CHAR-09')}{" "}{free}</span> : null}
          </button>
        </div>
      </div>
    </>
  );
}

/** Предел одной характеристики 5 + 2·(уровень − 1) (ГД 01 §3). */
export const statCap = (level: number) => 5 + 2 * (level - 1);

/** «Раздать за меня» по эталону: Сила, Живучесть, Сноровка по кругу (ГД 01 §3, T05: 6/6/6). */
export function autoSpend(free: number, stats: Record<(typeof STAT_KEYS)[number], number>, level: number) {
  const order = ['str', 'vit', 'agi'] as const;
  const draft = { str: 0, agi: 0, vit: 0, cha: 0 };
  let left = free;
  for (let i = 0; left > 0 && i < 100; i++) {
    const k = order[i % 3]!;
    if (stats[k] + draft[k] < statCap(level)) { draft[k]++; left--; }
  }
  return draft;
}

export function StatsSheet({ entry }: SheetProps) {
  const app = useApp();
  const me = player.value;
  const [draft, setDraft] = useState({ str: 0, agi: 0, vit: 0, cha: 0 });
  const key = useRef(app.idem());
  if (!me) return null;
  const used = draft.str + draft.agi + draft.vit + draft.cha;
  const free = me.statPointsFree - used;
  const cap = statCap(me.level);
  const save = async (d = draft) => {
    try {
      const p = await app.call('spendStats', { body: d, idempotencyKey: key.current });
      player.value = p;
      saveCache();
      key.current = app.idem();
      setDraft({ str: 0, agi: 0, vit: 0, cha: 0 });
      app.haptic('success');
    } catch (e) {
      app.handleError(e, { resource: tr('CL-SCREENS-CHAR-10') });
      setDraft({ str: 0, agi: 0, vit: 0, cha: 0 }); // черновик сбрасывается к серверу (NOT_ENOUGH_POINTS)
      app.call('getMe').then((p) => { player.value = p; }).catch(() => {});
    }
  };
  const actions = used > 0
    ? [{ text: tr('CL-SCREENS-CHAR-11'), tone: 'outline' as const, onClick: () => setDraft({ str: 0, agi: 0, vit: 0, cha: 0 }) }, { text: tr('UI-BTN-SAVE'), testId: 'stats-save', disabled: !online.value, onClick: () => save() }]
    : me.statPointsFree > 0
      ? [{ text: tr('UI-STATS-AUTO'), testId: 'stats-auto', disabled: !online.value, onClick: () => save(autoSpend(me.statPointsFree, me.stats, me.level)) }]
      : [{ text: tr('CL-SCREENS-CHAR-12'), tone: 'outline' as const, onClick: () => app.nav.popSheet() }];
  return (
    <Sheet entry={entry} title={tr('CL-SCREENS-CHAR-13')} actions={actions}>
      <div class="col">
        <div style={{ fontSize: 22, fontWeight: 800 }} data-testid="stats-free">{tr('CL-SCREENS-CHAR-14')}{" "}{free}</div>
        {STAT_KEYS.map((k) => (
          <div key={k} class="row card" style={{ padding: '6px 10px' }}>
            <span style={{ flex: 1 }}>{STAT_LABEL[k]} <span class="small muted">{me.stats[k] + draft[k]} / {cap}</span></span>
            <button class="btn small outline" style={{ width: 44 }} aria-label={tr('CL-SCREENS-CHAR-15', { p0: STAT_LABEL[k] })} disabled={draft[k] === 0} onClick={() => setDraft({ ...draft, [k]: draft[k] - 1 })}>−</button>
            <b style={{ width: 28, textAlign: 'center' }} class={draft[k] ? 'pos' : ''}>{draft[k] ? `+${draft[k]}` : ''}</b>
            <button class="btn small outline" style={{ width: 44 }} data-testid={`stat-plus-${k}`} aria-label={tr('CL-SCREENS-CHAR-16', { p0: STAT_LABEL[k] })} disabled={free <= 0 || me.stats[k] + draft[k] >= cap} onClick={() => { app.haptic('selection'); setDraft({ ...draft, [k]: draft[k] + 1 }); }}>+</button>
          </div>
        ))}
        <div class={`banner small cha-hint ${me.stats.cha > 5 ? 'warn' : ''}`} data-testid="cha-hint">{tr('CL-STATS-CHA-HINT')}</div>
        <p class="small muted" style={{ margin: 0 }}>{tr('CL-SCREENS-CHAR-17')}</p>
        <RespecLink />
      </div>
    </Sheet>
  );
}

/** Что открылось на уровнях (from, to]: приёмы и боссы (ux/03 §6; ГД 01 §5). */
export function unlocksBetween(from: number, to: number, cfg: Record<string, unknown> = {}): string[] {
  const out: string[] = [];
  for (const [id, lvl] of Object.entries(MOVE_LEVELS)) if (lvl > from && lvl <= to) out.push(tr('CL-SCREENS-CHAR-18', { p0: MOVES[id as keyof typeof MOVES].name }));
  for (const id of Object.keys(BOSSES)) {
    if (id.endsWith('-T')) continue;
    const lvl = bossInfo(id, cfg).level;
    if (lvl > from && lvl <= to) out.push(tr('CL-SCREENS-CHAR-19', { p0: BOSSES[id]!.name }));
  }
  return out;
}

export function LevelUpSheet({ entry }: SheetProps) {
  const app = useApp();
  const from = Number(entry.params['from']);
  const to = Number(entry.params['to']);
  const opened = unlocksBetween(from, to, clientConfig.value);
  useEffect(() => { app.haptic('success'); }, []);
  return (
    <Sheet entry={entry} actions={[
      { text: tr('UI-BTN-LATER'), tone: 'outline', onClick: () => app.nav.popSheet() },
      { text: tr('CL-SCREENS-CHAR-04'), testId: 'lvl-spend', onClick: () => app.nav.openSheet('MOD-STATS', {}, { replace: true }) },
    ]}>
      <div class="col levelup" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="level-up">
        {/* Короткая сцена (≤ 1.2 с, один раз): лучи и кольцо из-за значка, цифра уровня «набегает» с прежнего. */}
        <div class="lvl-stage" aria-hidden>
          <i class="lvl-rays" /><i class="lvl-ring" />
          <span class="lvl-num"><Count value={to} from={from} delay={350} duration={500} bump={false} format={String} /></span>
          <span class="lvl-spark"><I n="sparks" size={40} /></span>
        </div>
        <h2 style={{ margin: 0, fontSize: 28 }}>{to - from > 1 ? tr('CL-SCREENS-CHAR-20', { p0: from + 1, p1: to }) : tr('CL-SCREENS-CHAR-21', { p0: to })}</h2>
        <div class="badge reward-pop" style={{ '--i': 4 }}>+{3 * (to - from)} {" "}{tr('CL-SCREENS-CHAR-22')}</div>
        {opened.length ? <div class="card col" style={{ width: '100%', textAlign: 'left' }}><b>{tr('CL-SCREENS-CHAR-23')}</b>{opened.map((o) => <span key={o}>• {o}</span>)}</div> : null}
      </div>
    </Sheet>
  );
}

export { itemName };
